/**
 * MFASetup — Phase 9.2.
 *
 * Three states:
 *   1) NOT ENROLLED  → "Set up MFA" button → POST /mfa/enroll → shows QR + backup codes
 *   2) ENROLLED but NOT VERIFIED → user must enter first code to enable
 *   3) ENABLED → status card + "Disable MFA" flow (requires valid code)
 *
 * Backup codes are shown ONCE. UI enforces "I've saved my codes" before continuing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield, ShieldCheck, ShieldOff, Loader2, KeyRound, Copy, CheckCircle2,
  AlertTriangle, ClipboardCopy,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function MFASetup() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [enrolled, setEnrolled] = useState(null);  // { qr_code_base64, backup_codes, ... }
  const [savedCodes, setSavedCodes] = useState(false);
  const [code, setCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/mfa/status');
      setStatus(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load MFA status');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const beginEnroll = async () => {
    setEnrollBusy(true);
    try {
      const r = await api.post('/mfa/enroll');
      setEnrolled(r.data);
      setSavedCodes(false);
      setCode('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Enrolment failed');
    } finally { setEnrollBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length < 6) { toast.error('Enter the 6-digit code from your authenticator'); return; }
    setVerifyBusy(true);
    try {
      await api.post('/mfa/verify', { token: code.trim() });
      toast.success('MFA enabled successfully!');
      setEnrolled(null);
      setCode('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
    } finally { setVerifyBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm('Disable MFA? This lowers your account security.')) return;
    const token = window.prompt('Enter a current MFA code (or backup code) to confirm:');
    if (!token) return;
    try {
      await api.post('/mfa/disable', { token });
      toast.success('MFA disabled');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Disable failed');
    }
  };

  const copyCodes = () => {
    if (!enrolled?.backup_codes) return;
    navigator.clipboard.writeText(enrolled.backup_codes.join('\n'));
    toast.success('Backup codes copied');
  };

  const isEnabled = status?.enabled;
  const isEnrolledNotEnabled = status?.enrolled && !status?.enabled;

  const header = useMemo(() => {
    if (isEnabled)  return { icon: ShieldCheck, tone: 'text-emerald-600', bg: 'bg-emerald-500/10',
                             label: 'MFA is enabled', desc: 'Two-factor authentication is protecting your account.' };
    return { icon: Shield, tone: 'text-amber-600', bg: 'bg-amber-500/10',
             label: 'MFA not enabled', desc: 'Add a second factor to secure your account.' };
  }, [isEnabled]);

  return (
    <div className="p-6 pb-32 max-w-3xl mx-auto space-y-6" data-testid="mfa-setup-page">
      <div>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <h1 className="text-2xl font-heading font-bold" data-testid="mfa-setup-title">
            Multi-factor authentication
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          TOTP · works with Google Authenticator, Authy, 1Password, and Microsoft Authenticator.
        </p>
      </div>

      {loading ? (
        <Card className="p-6 text-center text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
        </Card>
      ) : (
        <>
          <Card className="p-5" data-testid="mfa-status-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg ${header.bg} flex items-center justify-center shrink-0`}>
                  <header.icon className={`w-5 h-5 ${header.tone}`} />
                </div>
                <div>
                  <div className="font-heading font-semibold text-sm" data-testid="mfa-status-label">{header.label}</div>
                  <div className="text-xs text-muted-foreground">{header.desc}</div>
                  {isEnabled && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Backup codes remaining: <span className="font-mono font-semibold">{status.backup_codes_remaining ?? 0}</span>
                    </div>
                  )}
                </div>
              </div>
              {isEnabled ? (
                <Button variant="destructive" size="sm" onClick={disable} data-testid="mfa-disable-btn">
                  <ShieldOff className="w-3.5 h-3.5 mr-1" /> Disable
                </Button>
              ) : (
                <Button size="sm" onClick={beginEnroll} disabled={enrollBusy} data-testid="mfa-enroll-btn">
                  {enrollBusy
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> …</>
                    : <><KeyRound className="w-3.5 h-3.5 mr-1"/> {isEnrolledNotEnabled ? 'Re-enrol' : 'Set up MFA'}</>}
                </Button>
              )}
            </div>
          </Card>

          {/* Enrolment flow */}
          {enrolled && (
            <Card className="p-5 space-y-5" data-testid="mfa-enroll-card">
              <div>
                <div className="text-sm font-heading font-semibold">1. Scan the QR with your authenticator</div>
                <div className="text-[11px] text-muted-foreground">
                  Or add the account manually using the provisioning URI below.
                </div>
                <div className="mt-3 flex flex-col md:flex-row gap-4 items-start">
                  <img
                    src={`data:image/png;base64,${enrolled.qr_code_base64}`}
                    alt="MFA QR"
                    className="w-44 h-44 rounded border bg-white"
                    data-testid="mfa-qr-image"
                  />
                  <div className="text-[11px] font-mono break-all bg-muted/50 p-2 rounded flex-1"
                       data-testid="mfa-provisioning-uri">
                    {enrolled.provisioning_uri}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-heading font-semibold">2. Save these backup codes</div>
                    <div className="text-[11px] text-muted-foreground">
                      Shown once. Each code works one time if you lose your device.
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={copyCodes} data-testid="mfa-copy-codes-btn">
                    <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-sm" data-testid="mfa-backup-codes">
                  {enrolled.backup_codes.map((c) => (
                    <div key={c} className="px-2 py-1.5 rounded border bg-muted/40 text-center tracking-wider">
                      {c}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Checkbox
                    id="mfa-saved"
                    checked={savedCodes}
                    onCheckedChange={(v) => setSavedCodes(!!v)}
                    data-testid="mfa-codes-saved-check"
                  />
                  <label htmlFor="mfa-saved" className="text-xs">I've saved my backup codes somewhere safe</label>
                </div>
              </div>

              <div>
                <div className="text-sm font-heading font-semibold">3. Enter the 6-digit code to enable</div>
                <div className="mt-2 flex gap-2 items-center">
                  <Input
                    value={code} onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric" maxLength={6}
                    placeholder="000000"
                    className="text-center text-lg tracking-widest font-mono max-w-[180px]"
                    data-testid="mfa-code-input"
                  />
                  <Button
                    onClick={verify}
                    disabled={verifyBusy || !savedCodes || code.trim().length < 6}
                    data-testid="mfa-verify-btn"
                  >
                    {verifyBusy
                      ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Verifying…</>
                      : <><CheckCircle2 className="w-4 h-4 mr-1"/> Enable MFA</>}
                  </Button>
                </div>
                {!savedCodes && (
                  <div className="mt-2 text-[11px] text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3"/> Confirm the checkbox above before enabling.
                  </div>
                )}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
